/* The details typed on `/giveaway-offer`, handed to the confirmation page.
 *
 * WHY THIS EXISTS. The confirmation page now offers a subscription checkout, and
 * Stripe's form needs a name, email, phone and address. Those were typed one
 * screen earlier — asking for them again is exactly the friction that loses a
 * signup someone started by scanning a poster.
 *
 * NONE OF THIS IS IDENTITY, and nothing here is trusted as such. The server
 * resolves the account from the EMAIL it is sent and re-validates every field in
 * `ensureCustomerAndUser`, so a tampered draft buys nothing: it can only pre-fill
 * a form that the server then checks anyway.
 *
 * sessionStorage rather than router state so that a reload on the confirmation
 * page — or a return from a Stripe redirect — does not strand the buyer with a
 * checkout it cannot fill in.
 *
 * Mirrors `features/PartnerSignup/partnerSignupFlowState.ts`, which solves the
 * same problem for the funnel next door.
 */

export type GiveawayOfferDraft = {
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;
  phone?: string;
  countryIso2?: string;
  countryName?: string;
  stateIso2?: string;
  stateName?: string;
  phoneCountryCode?: string;
  agreeToTerms?: boolean;
  eligible?: 'yes' | 'no' | '';
};

const DRAFT_KEY = 'giveawayOfferDraft';

export const saveGiveawayOfferDraft = (draft: GiveawayOfferDraft) => {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* Private browsing, or a full quota. Not worth failing a completed signup
       over: the confirmation page falls back to sending the visitor to
       /giveaway-subs, which collects these details itself. */
  }
};

export const getGiveawayOfferDraft = (): GiveawayOfferDraft | null => {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as GiveawayOfferDraft) : null;
  } catch {
    return null;
  }
};

export const clearGiveawayOfferDraft = () => {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* Nothing to clear. */
  }
};
